import {
    TextField as _TextField,
    InputProps,
    Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {getValueFromNestedObject} from "../../../../utils/langUtils.ts";
import {transformIdOptionType, IdOptionType, TransformOptionType} from "./FormSelect.tsx";

const TextField = styled(_TextField)`
    padding: 0.4rem 0.7rem;
`;

export type AsyncFormSelectProps = {
    name: string;
    label: string;
    options: Array<IdOptionType> | undefined;
    optionsLoading: boolean;
    multiple?: boolean;
    transform: TransformOptionType<IdOptionType>;
    valueMap?: (option: IdOptionType | undefined) => any;
    optionLabelMap?: (option: IdOptionType | undefined) => string;
    onValueChange?: (option: IdOptionType | IdOptionType[] | undefined) => void;
    onInputChange?: (value: string | undefined) => void;
} & InputProps;

const AsyncFormSelect: FC<AsyncFormSelectProps> = ({
                                                       name,
                                                       label,
                                                       options = [],
                                                       optionsLoading,
                                                       multiple,
                                                       required,
                                                       transform = transformIdOptionType,
                                                       valueMap,
                                                       optionLabelMap,
                                                       onValueChange,
                                                       onInputChange,
                                                       ...otherProps
                                                   }) => {
    const {
        control,
        formState: {errors},
    } = useFormContext();

    const error = getValueFromNestedObject(errors, name)

    return (
        <Controller
            control={control}
            name={name}
            render={({field: props}) => {

                const valueData = valueMap && valueMap(props.value)

                return (<Autocomplete
                    fullWidth
                    disablePortal
                    filterOptions={(x) => x}
                    options={options}
                    loading={optionsLoading}
                    multiple={multiple}
                    getOptionKey={transform.outputToKey}
                    getOptionLabel={(optionValue) => {
                        if (optionValue == null) {
                            return '';
                        } else if (typeof optionValue === 'object') {
                            return optionLabelMap && optionValue.id ? optionLabelMap(optionValue) : '';
                        } else {
                            const value = {id: optionValue}
                            const foundOption = options.find((o) => transform.optionEqualToValue(o, value));
                            const optionLabel = optionLabelMap ? optionLabelMap(foundOption) || '' : '';
                            if (!optionLabel && valueData?.data) {
                                return optionLabelMap(valueData.data) || ''
                            }
                            return optionLabel
                        }
                    }}
                    isOptionEqualToValue={multiple ? transform.multipleOptionEqualToValue : transform.optionEqualToValue}
                    onChange={(_, newValue) => {
                        props.onChange(
                            multiple ?
                                newValue?.map((input) => {
                                    if (typeof input === 'object') {
                                        return transform.outputToKey(input)
                                    }
                                    return input
                                })
                                :
                                transform.outputToKey(newValue)
                        )
                        onValueChange && onValueChange(newValue ?? undefined)
                    }}
                    onInputChange={(_, newInputValue) => {
                        onInputChange ? onInputChange(newInputValue ?? undefined) : undefined;
                    }}
                    value={multiple ? (props.value ?? []) : (props.value ?? null)}
                    {...otherProps}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            margin="normal"
                            error={!!error}
                            variant="standard"
                            label={label}
                            required={required}
                            helperText={
                                error ? (error?.message as unknown as string) : ''
                            }
                        />
                    )
                    }
                />)
            }}
        />
    );
};

export default AsyncFormSelect;
